import type { IncomeSource, MonthlyLedgerEntry } from '@/types'
import type { ILedgerRepository } from './ledgerTypes'
import { isIncomeSourcesArray, isLedgerEntriesArray } from './validators'

const INCOME_KEY = 'debtos:incomeSources:v1'
const LEDGER_KEY = 'debtos:ledger:v1'

export class LocalStorageLedgerRepo implements ILedgerRepository {
  // ─── Generic helpers ───────────────────────────────────────────────
  /**
   * Read + validate. Returns the parsed payload when the shape matches
   * the runtime `validator`; otherwise falls back to the provided value
   * (almost always `[]` for these repos) and logs a warning. The cast
   * is type-safe because `validator` is a TS predicate that narrows the
   * union to the expected entity-array type.
   */
  private readValidated<T>(key: string, validator: (v: unknown) => v is T, fallback: T): T {
    try {
      const raw = localStorage.getItem(key)
      if (!raw) return fallback
      const parsed: unknown = JSON.parse(raw)
      if (!validator(parsed)) {
        console.warn(
          `[debtos] Malformed payload at ${key}; resetting to default. ` +
            'Most likely caused by a restore from an incompatible backup.'
        )
        return fallback
      }
      return parsed
    } catch {
      return fallback
    }
  }

  /**
   * Throws on any `setItem` failure (QuotaExceededError under Safari private
   * mode, near-full Android storage, large-ledger pushes) so callers can
   * surface the problem to the user instead of letting in-memory state drift
   * silently ahead of disk.
   */
  private writeJson(key: string, value: unknown): void {
    try {
      localStorage.setItem(key, JSON.stringify(value))
    } catch (err) {
      throw new Error(
        `Failed to save to local storage (${key}): ` +
          (err instanceof Error ? err.message : String(err))
      )
    }
  }

  // ─── Income sources ────────────────────────────────────────────────
  async getAllIncomeSources(): Promise<IncomeSource[]> {
    return this.readValidated<IncomeSource[]>(INCOME_KEY, isIncomeSourcesArray, [])
  }

  async getIncomeSourceById(id: string): Promise<IncomeSource | null> {
    const sources = await this.getAllIncomeSources()
    return sources.find((s) => s.id === id) ?? null
  }

  async upsertIncomeSource(source: IncomeSource): Promise<IncomeSource> {
    const sources = await this.getAllIncomeSources()
    const idx = sources.findIndex((s) => s.id === source.id)
    const updated = { ...source, updatedAt: new Date().toISOString() }
    if (idx >= 0) {
      sources[idx] = updated
    } else {
      sources.push(updated)
    }
    this.writeJson(INCOME_KEY, sources)
    return updated
  }

  async deleteIncomeSource(id: string): Promise<void> {
    const sources = (await this.getAllIncomeSources()).filter((s) => s.id !== id)
    this.writeJson(INCOME_KEY, sources)
  }

  async bulkReplaceIncomeSources(sources: IncomeSource[]): Promise<void> {
    this.writeJson(INCOME_KEY, sources)
  }

  // ─── Ledger entries ────────────────────────────────────────────────
  async getAllLedgerEntries(): Promise<MonthlyLedgerEntry[]> {
    const entries = this.readValidated<MonthlyLedgerEntry[]>(LEDGER_KEY, isLedgerEntriesArray, [])
    // Always return sorted by month ascending — UI relies on this order
    return [...entries].sort((a, b) => a.month.localeCompare(b.month))
  }

  async getLedgerEntryByMonth(month: string): Promise<MonthlyLedgerEntry | null> {
    const entries = await this.getAllLedgerEntries()
    return entries.find((e) => e.month === month) ?? null
  }

  async upsertLedgerEntry(entry: MonthlyLedgerEntry): Promise<MonthlyLedgerEntry> {
    const entries = await this.getAllLedgerEntries()
    // Enforce one-entry-per-month invariant
    const idx = entries.findIndex((e) => e.month === entry.month)
    if (idx >= 0) {
      entries[idx] = entry
    } else {
      entries.push(entry)
    }
    this.writeJson(LEDGER_KEY, entries)
    return entry
  }

  /** v0.8.56 — one device, nothing to merge: store the new version. */
  async saveLedgerChanges(_before: MonthlyLedgerEntry, after: MonthlyLedgerEntry): Promise<void> {
    await this.upsertLedgerEntry(after)
  }

  async deleteLedgerEntry(id: string): Promise<void> {
    const entries = (await this.getAllLedgerEntries()).filter((e) => e.id !== id)
    this.writeJson(LEDGER_KEY, entries)
  }

  async bulkReplaceLedgerEntries(entries: MonthlyLedgerEntry[]): Promise<void> {
    this.writeJson(LEDGER_KEY, entries)
  }
}
